<template>
  <div>
    <ControlGroup>
      <DataGetter
        :url="`${url}/list/Country`"
        :params="{ CODE_INDICATOR: ['HEATHOUSE', 'SHOWER', 'CARS', 'COOKING'] }"
        v-slot="{ data }"
        :child-element-type="null"
        reset-on-invalid
        lazy
      >
        <Dropdown
          v-if="data"
          label="Country/region"
          :options="data"
          v-model="country"
          reset-on-invalid
        />
      </DataGetter>
      <DataGetter
        :url="`${url}/list/CODE_YEAR`"
        :params="{
          CODE_INDICATOR: ['HEATHOUSE', 'SHOWER', 'CARS', 'COOKING'],
          Country: country,
        }"
        v-slot="{ data }"
        :child-element-type="null"
        lazy
        @fetched="
          (data) => {
            if (alwaysMostRecent) year = data[data.length - 1];
          }
        "
      >
        <Dropdown
          v-if="data"
          label="Year"
          :options="[...data].reverse()"
          v-model="year"
          reset-on-invalid
        />
      </DataGetter>
    </ControlGroup>
    <ControlGroup right>
      <Toggle
        label="Always show most recent"
        small
        v-model="alwaysMostRecent"
      />
    </ControlGroup>
    <h4 class="f-title-7">Typical cost to...</h4>
    <DataGetter
      :url="url"
      :params="{
        CODE_INDICATOR: ['HEATHOUSE', 'SHOWER', 'CARS', 'COOKING'],
        Country: country,
        CODE_YEAR: year,
      }"
      :initial-data="[]"
      lazy
      v-slot="{ data }"
      :child-element-type="null"
    >
      <DataSlicer
        :data="data"
        xAxisDimension="Product"
        yAxisDimension="CODE_INDICATOR"
        valueDimension="Value"
        v-slot="{ slicedData }"
        class="indicators-container"
      >
        <Chart
          v-for="series in slicedData
            .slice(0)
            .sort(
              (a, b) =>
                Object.values(indicatorNames).indexOf(a.name) -
                Object.values(indicatorNames).indexOf(b.name)
            )"
          :key="series.name"
          :title="indicatorNames[series.name]"
          :units="`National currency, ${year} energy prices`"
          :series="[
            {
              ...series,
              name: indicatorNames[series.name],
              colorByPoint: true,
              dataLabels: {
                enabled: true,
                inside: false,
                formatter: function () {
                  const { point } = this;
                  const change =
                    (point.y - point.series.dataMin) / point.series.dataMin;
                  return point.x === 0
                    ? `${point.y.toFixed(2)}`
                    : `${point.y.toFixed(2)}<br>(+${(change * 100).toFixed(
                        0
                      )}%)`;
                } /*
                format:
                  '{point.y:.2f} {point.y - point.series.dataMin / point.series.dataMin}',
                  */,
              },
              data: series.data
                .filter((row) => row[1] !== null)
                .sort((a, b) => a[1] - b[1])
                .map((point, i) => ({
                  name: point[0],
                  x: i,
                  y: point[1],
                  colorIndex: pointColor[point[0]],
                })),
            },
          ]"
          :chartOptions="{
            chart: {
              height: 240,
              spacing: 0,
              margin: [10, 0, 60, 60],
            },
            xAxis: { type: 'category' },
            yAxis: {
              enabled: true,
            },
          }"
          chart-type="column"
          legend-disabled
          :credit="null"
        />
      </DataSlicer>
    </DataGetter>
    <Block>
      <p>
        Expenditures presented here cover operational fuel costs only, and do
        not include any other upfront or regular costs necessary to run the
        equipment.
      </p>
      <p>
        Estimations are based on IEA Energy Prices and
        <a
          href="https://www.iea.org/data-and-statistics/data-tools/energy-efficiency-indicators-data-explorer"
          >Energy Efficiency Indicators</a
        >
        databases, combining end-uses energy consumptions by country, year and
        sector and prices.
      </p>
      <p>
        Space heating estimates are computed multiplying average
        temperature-corrected energy intensity of heating by country and year
        and fuel prices. No technology-specific efficiencies are used.
      </p>
      <p>
        Data for heat pumps are presented assuming a Seasonal Performance Factor
        (SPF) of 3 that is typical of Air-Air heat pumps in mild climates, but
        this can vary depending on weather conditions, type of heat pump used
        and efficiency of the heat pump. For more detail about heating systems
        costs, please refer to the
        <a
          href="https://www.iea.org/data-and-statistics/data-tools/residential-heat-economics-calculator"
          >Residential Heat Economics Calculator</a
        >.
      </p>
      <p>
        Car fuel expenditures are computed using diesel and gasoline specific
        energy intensities by country and year, multiplied by annual car mileage
        in the country/year. Data for electric vehicles is presented assuming a
        consumption of 0.15 kWh/km, but this figure can vary depending on the
        size of the vehicle, its efficiency and weather conditions. For most
        details on EVs total costs please refer to the
        <a
          href="https://www.iea.org/data-and-statistics/data-tools/electric-vehicles-total-cost-of-ownership-tool"
          >Electric Vehicles Total Cost of Ownership Tool</a
        >.
      </p>
    </Block>
  </div>
</template>

<script>
import { Chart } from "@international-energy-agency/vue-ui/highcharts";
import {
  ControlGroup,
  Dropdown,
  Toggle,
  DataGetter,
  DataSlicer,
  Block,
} from "@international-energy-agency/vue-ui";
import countryMixin from "./countryMixin";

export default {
  name: "ExpenditureIndicators",
  components: {
    ControlGroup,
    Dropdown,
    Toggle,
    DataGetter,
    DataSlicer,
    Chart,
    Block,
  },
  props: {
    url: {
      type: String,
      default: "https://api.iea.org/prices",
      required: false,
    },
  },
  mixins: [countryMixin],
  data() {
    return {
      year: "2020",
      pointColor: {
        "Heat pump": 8,
        "Natural gas": 0,
        "Liquefied petroleum gases (LPGs)": 1,
        Electricity: 4,
        Kerosene: 5,
        "Automotive diesel ": 3,
        "Motor gasoline": 2,
      },
      indicatorNames: {
        CARS: "Fuel a car for a year",
        HEATHOUSE: "Heat a square metre for a year",
        SHOWER: "Heat water for showers for a year",
        COOKING: "Cook for a year",
      },
      alwaysMostRecent: true,
    };
  },
};
</script>

<style scoped>
.indicators-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 20px;
}
@media screen and (max-width: 1200px) {
  .indicators-container {
    display: block;
  }
}
</style>